import styles from './VisitTimeline.module.scss';

import * as React from 'react';
import * as _ from 'lodash';
import moment from 'moment-timezone';

import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../redux/types';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { css } from '../../util/Css';
import { PageHeader } from '../core/PageHeader';
import { Callout, Dropdown, IconButton, Link, MessageBar, MessageBarType, Persona, PersonaSize, Spinner, SpinnerSize, Stack, TooltipHost } from '@fluentui/react';
import { DateSelector } from '../core/DateSelector';
import { useQueryString } from '../../util/hooks';
import { fetchSchedule, fetchShifts } from '../../api/scheduling';
import { Shift, ShiftSchedule } from '../../api/scheduling.types';
import { PageSidePanel } from '../core/PageSidePanel';
import { AsyncVisitSidePanel } from '../core/AsyncVisitSidePanel';
import { ROUTES } from '../../config/routes';

const worldWidth = 2000;
const millisecondInDay = 86400000;
const ratio = worldWidth / millisecondInDay;

const beginOfDay = moment().startOf('day').valueOf();

export interface Item {
  actual_start: boolean;
  actual_duration: boolean;
  start_time: number;
  type: 'start_day' | 'drive' | 'visit' | 'break' | 'end_day';
  details: any;
  actual_enroute: boolean;
  enroute_time: number;
  duration: number;
}
export interface ScheduleMap extends Record<string, Item[]> {}

export const VisitTimeline = () => {
  const dispatch = useDispatch();
  const [fetching, setFetching] = useState(true);
  const [date, setDate] = useQueryString('date', moment().format('YYYY-MM-DD'));
  const [schedule, setSchedule] = useState<ScheduleMap>({});
  const [shifts, setShifts] = useState<ShiftSchedule>();
  const [groupCode, setGroupCode] = useQueryString('group', 'LA');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [selectedVisitCode, setSelectedVisitCode] = useState('');

  const allMarkets = useSelector((state: RootState) => state.markets.allMarkets);

  const groupOptions = useMemo(() => {
    const groupCodes = _.groupBy(
      (allMarkets.data || []).filter((m) => m.active),
      'groupCode'
    );
    return _.keys(groupCodes).map((key) => ({
      key,
      text: `${key} - (${groupCodes[key]
        .map((g) => g.code)
        .sort()
        .join(',')})`,
    }));
  }, [allMarkets]);

  const onSelect = (item: Item) => {
    setSelectedVisitCode(item.details.visit_code);
  };

  useEffect(() => {
    if (allMarkets.data) {
      setTimezone(allMarkets.data!.find((m) => m.groupCode === groupCode)?.timeZone || 'America/Los_Angeles');
    }
  }, [groupCode, allMarkets.data]);

  useEffect(() => {
    dispatch(onPageDidMount('VisitsTimeline'));
    return () => {
      dispatch(onPageWillUnmount('VisitsTimeline'));
    };
  }, [dispatch]);

  useEffect(() => {
    let canceled = false;
    setFetching(true);

    const fetch = [
      fetchShifts(date).then((data) => {
        if (!canceled) {
          setShifts(data);
        }
      }),
      fetchSchedule(date, groupCode).then((data) => {
        if (!canceled) {
          setSchedule(data);
        }
      }),
    ];
    Promise.all(fetch).then(() => setFetching(false));
    return () => {
      canceled = true;
    };
  }, [date, groupCode]);

  return (
    <>
      <PageHeader title="Visits Timeline" isLoading={fetching} showShareButton={true} />
      <Stack horizontal={true} verticalAlign="end" tokens={{ childrenGap: 10 }}>
        <Dropdown label="Market Group" placeholder="Select group" options={groupOptions} selectedKey={groupCode} onChange={(a, v) => v && setGroupCode(v.key as string)} />
        <DateSelector outputFormat="YYYY-MM-DD" defaultDate={moment(date).toDate()} showNudgeButtons={true} onChange={(d) => setDate(d)} />
        <HelpButton />
      </Stack>
      {fetching && <Spinner size={SpinnerSize.large} label="Fetching schedules..." />}
      {!fetching && (
        <div className={styles.container} style={{ width: worldWidth }}>
          <TimeslotOverlay timezone={timezone} />
          {Object.keys(schedule).map((id) => {
            const providerShifts = [...shifts!.am.shifts.filter((s) => s.doctor?.id === id), ...shifts!.pm.shifts.filter((s) => s.doctor?.id === id)];
            return <ProviderTimeline key={id} id={id} schedule={schedule[id]} date={date} shifts={providerShifts} onSelect={onSelect} />;
          })}
          <TodayLine date={date} timezone={timezone} />
        </div>
      )}
      {!fetching && _.keys(schedule).length === 0 && <MessageBar messageBarType={MessageBarType.info}>There are no visits for this group on this date.</MessageBar>}

      {selectedVisitCode && (
        <PageSidePanel>
          <AsyncVisitSidePanel visitCode={selectedVisitCode} onClose={() => setSelectedVisitCode('')} />
        </PageSidePanel>
      )}
    </>
  );
};

const ProviderTimeline = ({ id, schedule, date, shifts, onSelect }: { id: string; schedule: Item[]; date: string; shifts: Shift[]; onSelect: (item: Item) => void }) => {
  if (!shifts || shifts.length === 0) {
    // Hide providers that do not have shifts.
    return null;
  }
  return (
    <div key={id} className={styles.timeline}>
      <ProviderDetails id={id} date={date} shifts={shifts} />
      <ScheduleTrack date={date} start={schedule.find((s) => s.type === 'start_day')!.start_time} end={schedule.find((s) => s.type === 'end_day')!.start_time} shifts={shifts} />
      <SegmentItems date={date} items={schedule} onSelect={onSelect} />
    </div>
  );
};

const TodayLine = ({ date, timezone }: { date: string; timezone: string }) => {
  if (!moment(date).isSame(moment(), 'day')) {
    return null;
  }
  return (
    <div className={styles.todayLine} style={{ left: (moment().valueOf() - beginOfDay) * ratio }}>
      <div className={styles.todayTime}>{moment().tz(timezone).format('h:mm a')}</div>
    </div>
  );
};

const TimeslotOverlay = ({ timezone }: { timezone: string }) => {
  const tzBeginOfDay = moment().startOf('day').valueOf();

  return (
    <>
      {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((hour) => (
        <div
          key={`timeslot_line_${hour}`}
          className={styles.timeslotLine}
          style={{
            left: (moment().tz(timezone, true).hours(hour).minutes(0).valueOf() - tzBeginOfDay) * ratio,
          }}
        />
      ))}
      <div className={styles.header}>
        {[
          [8, 9],
          [9, 10],
          [10, 11],
          [11, 12],
          [13, 14],
          [14, 15],
          [15, 16],
          [16, 17],
          [17, 18],
        ].map(([start, end]) => (
          <div
            key={`timeslot_header_${start}`}
            className={styles.item}
            style={{
              left: (moment().tz(timezone, true).hours(start).minutes(0).valueOf() - tzBeginOfDay) * ratio,
              width: (moment().tz(timezone, true).hours(end).minutes(0).valueOf() - moment().tz(timezone, true).hours(start).minutes(0).valueOf()) * ratio,
            }}
          >
            {`${moment().tz(timezone, true).hours(start).minutes(0).format('h a')}`}
          </div>
        ))}
      </div>
    </>
  );
};

const ProviderDetails = ({ id, date, shifts }: { id: string; date: string; shifts: Shift[] }) => {
  const navigate = useNavigate();
  const wrapperDiv = React.useRef<HTMLDivElement>(null);
  const [openCallout, setOpenCallout] = useState(false);
  const providers = useSelector((state: RootState) => state.providers.allProviders);
  const provider = providers.data?.find((p) => p.id === id);
  if (!provider) {
    return <div>{id}</div>;
  }

  const providerMarketCodes = provider.providerMarkets.map((m) => m.marketCode);

  let shiftHours = '';
  let shiftMarkets = 'No shift found.';
  let error = false;

  if (shifts.length > 0) {
    shiftHours = `${moment(shifts[0].drDateRange.startTime * 1000)
      .tz(shifts[0].drDateRange.timeZone)
      .format('h:mm a')} - ${moment(shifts[0].drDateRange.endTime * 1000)
      .tz(shifts[0].drDateRange.timeZone)
      .format('h:mm a')}`;
    shiftMarkets = shifts[0].submarkets.join(', ');
    error = !shifts[0].submarkets.every((m) => providerMarketCodes.indexOf(m) !== -1);
  }

  return (
    <div className={styles.providerDetails}>
      <div className="flex justify-space-between">
        <Persona
          size={PersonaSize.size40}
          text={`${provider.prefix} ${provider.firstName} ${provider.lastName}`}
          secondaryText={`${shifts[0]?.positionType} | ${shiftHours}`}
          imageUrl={provider.avatarUrl}
        />
        <div className="flex">
          {error && (
            <div ref={wrapperDiv}>
              <IconButton style={{ color: 'red' }} iconProps={{ iconName: 'ErrorBadge' }} onClick={() => setOpenCallout(true)} />
            </div>
          )}
          <IconButton iconProps={{ iconName: 'ContactInfo' }} onClick={() => navigate(`${ROUTES.visitsProvider}?providerId=${id}&date=${date}`)} />
        </div>
      </div>
      <TooltipHost content={shiftMarkets}>
        <div className={styles.markets}>{shiftMarkets}</div>
      </TooltipHost>

      {openCallout && (
        <Callout target={wrapperDiv.current} beakWidth={0} onDismiss={() => setOpenCallout(!openCallout)}>
          <Stack style={{ padding: 20 }} tokens={{ childrenGap: 10 }}>
            <MessageBar messageBarType={MessageBarType.error}>Misconfiguration detected, provider missing one or more Market(s)</MessageBar>
            <div>These are the job sites in When I Work: {shiftMarkets}</div>
            <div>These are the markets the provider supports: {providerMarketCodes.sort().join(', ')}</div>
          </Stack>
        </Callout>
      )}
    </div>
  );
};

const ScheduleTrack = ({ date, start, end, shifts }: { date: string; start: number; end: number; shifts: Shift[] }) => {
  let durationInMS = end - start;
  let startTime = start;

  if (shifts.length > 0) {
    const momentStart = moment(shifts[0].drDateRange.startTime * 1000).tz(shifts[0].drDateRange.timeZone);
    const momentEnd = moment(shifts[0].drDateRange.endTime * 1000).tz(shifts[0].drDateRange.timeZone);

    startTime = momentStart.valueOf();
    durationInMS = momentEnd.valueOf() - momentStart.valueOf();
  }

  return (
    <div
      className={styles.schedule}
      style={{
        left: (startTime - moment(date).startOf('day').valueOf()) * ratio,
        width: durationInMS * ratio,
      }}
    />
  );
};

const SegmentItems = ({ date, items, onSelect }: { date: string; items: Item[]; onSelect: (item: Item) => void }) => {
  return (
    <>
      {items.map((i, ix) => (
        <SegmentItem key={ix} date={date} item={i} onSelect={onSelect} />
      ))}
    </>
  );
};

const SegmentItem = ({ date, item, onSelect }: { date: string; item: Item; onSelect: (item: Item) => void }) => {
  const [openDetails, setOpenDetails] = useState(false);
  const start = item.start_time;

  const mapMarketCodeToTimezone = useSelector((state: RootState) => Object.assign({}, ...(state.markets.allMarkets.data || []).map((m) => ({ [m.code]: m.timeZone }))));

  if (item.type === 'break') {
    return (
      <div
        className={css(styles.segment, styles.break)}
        style={{
          left: (start - moment(date).startOf('day').valueOf()) * ratio,
          width: item.duration * ratio,
        }}
      >
        Break
      </div>
    );
  }

  if (item.type === 'visit') {
    const isTelehealth = item.details.service_code.startsWith('TELEMED') || item.details.service_code.startsWith('THERAPY');
    const isFlushot = item.details.service_code.startsWith('FLUSHOT');
    const timezone = mapMarketCodeToTimezone[item.details.market_code];

    let durationInMS = item.duration;
    if (item.details.time_ended_visit > 0) {
      durationInMS = item.details.time_ended_visit - item.details.time_started_visit;
      durationInMS = Math.max(durationInMS, 300000);
    }

    const isCompleted = ['FULLY_PAID', 'REFUNDED'].indexOf(item.details.status) > -1;
    const isStarted = item.details.status === 'STARTED';
    const isOutOfTimeslot = moment(item.details.time_slot_end_time).isBefore(moment(item.start_time).tz(timezone));
    return (
      <>
        <div
          onClick={() => setOpenDetails(true)}
          className={css('visit-' + item.details.visit_code, styles.segment, styles.visit, {
            [styles.completed]: isCompleted,
            [styles.started]: isStarted,
            [styles.telehealth]: isTelehealth,
            [styles.flushot]: isFlushot,
            [styles.outoftimeslot]: isOutOfTimeslot,
          })}
          style={{
            left: (start - moment(date).startOf('day').valueOf()) * ratio,
            width: durationInMS * ratio,
          }}
        ></div>
        {openDetails && (
          <Callout gapSpace={0} target={`.visit-${item.details.visit_code}`} onDismiss={() => setOpenDetails(false)}>
            <Stack style={{ padding: 20 }}>
              <Link onClick={() => onSelect(item)} target="_blank">
                {item.details.visit_code}
              </Link>
              <div>{item.details.service_code}</div>
              <div>{item.details.status}</div>
              <div>{`${moment(item.start_time).tz(timezone).format('h:mm a')} - ${moment(item.start_time + item.duration)
                .tz(timezone)
                .format('h:mm a')}`}</div>
              <div>{item.details.longer_time_string}</div>
              <div>{item.details.address_long}</div>
              {/* <pre>{JSON.stringify(item, undefined, 2)}</pre> */}
            </Stack>
          </Callout>
        )}
      </>
    );
  }

  return null;
};

const HelpButton = () => {
  const [showHelp, setShowHelp] = useState(false);
  return (
    <>
      <IconButton className="helpbutton-target" iconProps={{ iconName: 'Help' }} onClick={() => setShowHelp(true)} />
      {showHelp && (
        <Callout target={`.helpbutton-target`} gapSpace={0} onDismiss={() => setShowHelp(false)}>
          <div className={styles.legend}>
            <table width="100%">
              <thead>
                <tr>
                  <td></td>
                  <td>Upcoming</td>
                  <td>Started</td>
                  <td>Completed</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>House visit</td>
                  <td align="center">
                    <div className={css(styles.key)} />
                  </td>
                  <td align="center">
                    <div className={css(styles.started, styles.key)} />
                  </td>
                  <td align="center">
                    <div className={css(styles.completed, styles.key)} />
                  </td>
                </tr>
                <tr>
                  <td>Telehealth call</td>
                  <td align="center">
                    <div className={css(styles.key, styles.telehealth)} />
                  </td>
                  <td align="center">
                    <div className={css(styles.started, styles.key, styles.telehealth)} />
                  </td>
                  <td align="center">
                    <div className={css(styles.completed, styles.key, styles.telehealth)} />
                  </td>
                </tr>
                <tr>
                  <td>Flu shot</td>
                  <td align="center">
                    <div className={css(styles.key, styles.flushot)} />
                  </td>
                  <td align="center">
                    <div className={css(styles.started, styles.key, styles.flushot)} />
                  </td>
                  <td align="center">
                    <div className={css(styles.completed, styles.key, styles.flushot)} />
                  </td>
                </tr>
              </tbody>
            </table>
            <br />
            <MessageBar>
              <div>
                When you see a visit with a border <div className={css(styles.key, styles.outoftimeslot)} />
              </div>
              <div>It means the predicted time does not reside within the booked timeslot window.</div>
              <div>The visit should be rescheduled into the appropriate window to improve capacity.</div>
            </MessageBar>
          </div>
        </Callout>
      )}
    </>
  );
};
